import { DeliveryPricingBands } from 'shop/types/cart'
import { formatMoney } from 'shop/utils/common'

/** Adds a dummy band if the first band doesn't start at 0 */
const addDummyBand = (
  bands: DeliveryPricingBands[]
): DeliveryPricingBands[] => {
  if (!bands.length) return bands

  const firstBand = bands[0]
  const firstThreshold = parseFloat(firstBand.lowerThreshold)

  if (firstThreshold === 0) return bands

  // upperThreshold ends where the first real band starts, also uses first real band's percentageDiscount as the goal to reach
  const dummyBand: DeliveryPricingBands = {
    id: 'dummy-band',
    lowerThreshold: '0.00',
    upperThreshold: firstBand.lowerThreshold,
    percentageDiscount: firstBand.percentageDiscount
  }

  return [dummyBand, ...bands]
}

const getCurrentPricingBand = (
  subtotal: string,
  bands: DeliveryPricingBands[]
) => {
  const subtotalValue = parseFloat(subtotal)
  return bands.find((band) => {
    const lower = parseFloat(band.lowerThreshold)

    // Final band with null upperThreshold means "and upwards"
    if (!band.upperThreshold) {
      return subtotalValue >= lower
    }

    const upper = parseFloat(band.upperThreshold)
    return subtotalValue >= lower && subtotalValue < upper
  })
}

const getNextPricingBand = (
  subtotalValue: number,
  bands: DeliveryPricingBands[]
) => {
  return bands.find((band) => parseFloat(band.lowerThreshold) > subtotalValue)
}

const getBelowThresholdMessage = (band: DeliveryPricingBands) =>
  `Spend ${formatMoney(band.lowerThreshold, { stripTrailingZeros: true })} to get ${band.percentageDiscount}% off delivery`

const getFinalThresholdMessage = (band: DeliveryPricingBands) =>
  `You've earned ${band.percentageDiscount}% off delivery`

const getBetweenThresholdsMessage = (
  subtotal: string,
  subtotalValue: number,
  bands: DeliveryPricingBands[]
) => {
  const currentBand = getCurrentPricingBand(subtotal, bands)
  const nextBand = getNextPricingBand(subtotalValue, bands)

  if (!currentBand || !nextBand) return null

  const amountToNext = parseFloat(nextBand.lowerThreshold) - subtotalValue
  const formattedAmount = formatMoney(amountToNext, {
    stripTrailingZeros: true
  })

  return `You've earned ${currentBand.percentageDiscount}% off delivery, add ${formattedAmount} more to get ${nextBand.percentageDiscount}% off.`
}

/** Determines delivery pricing band copy shown in the UI */
const getDiscountCopy = (
  subtotal: string,
  deliveryPricingBands: DeliveryPricingBands[]
) => {
  if (!subtotal || !deliveryPricingBands?.length) return null

  const subtotalValue = parseFloat(subtotal)

  const firstBand = deliveryPricingBands[0]
  const secondBand = deliveryPricingBands[1]
  const finalBand = deliveryPricingBands[deliveryPricingBands.length - 1]
  const finalThreshold = parseFloat(finalBand.lowerThreshold)

  // Handle case where there's only one band / handles special case where user does not use 'and upwards' in controls, have to manually check for deliveryPricingBands length
  if (!firstBand.upperThreshold || deliveryPricingBands.length === 1) {
    return subtotalValue > finalThreshold
      ? getFinalThresholdMessage(finalBand)
      : getBelowThresholdMessage(finalBand)
  }

  const firstThreshold = parseFloat(firstBand.upperThreshold)

  // Below first 'real' threshold
  if (subtotalValue < firstThreshold) {
    return getBelowThresholdMessage(secondBand)
  }

  // Above final threshold
  if (subtotalValue > finalThreshold) {
    return getFinalThresholdMessage(finalBand)
  }

  // Between thresholds
  return getBetweenThresholdsMessage(
    subtotal,
    subtotalValue,
    deliveryPricingBands
  )
}

/** Returns active bands (both 100% & in progress) */
const getActiveBands = (
  subtotal: string | undefined,
  deliveryPricingBands: DeliveryPricingBands[]
) => {
  if (!subtotal) return []
  const subtotalValue = parseFloat(subtotal)
  return deliveryPricingBands.filter(
    (band) => subtotalValue > parseFloat(band.lowerThreshold)
  )
}

/** Determines animation delay on delivery pricing band status change */
const calculateAnimationDelay = (
  isActive: boolean,
  progress: number,
  index: number,
  prevActiveCount: number,
  activeBandsCount: number,
  hasBandInProgress: boolean
) => {
  const isPartiallyFilled = progress > 0 && progress < 100
  const isFullyActive = isActive && (progress === 0 || progress === 100)
  const bandsBeingRemoved = prevActiveCount - activeBandsCount
  const bandsBeingAdded = activeBandsCount - prevActiveCount
  const isRemoving = bandsBeingRemoved > 0 && index >= activeBandsCount

  const removalPosition = isRemoving ? prevActiveCount - 1 - index : 0

  let animationDelay = 0

  if (bandsBeingAdded > 1 && isActive) {
    animationDelay = index * 500
  } else if (bandsBeingAdded === 1 && hasBandInProgress) {
    if (isFullyActive) {
      animationDelay = 0
    } else if (isPartiallyFilled) {
      animationDelay = 500
    }
  } else if (bandsBeingRemoved > 1) {
    if (isRemoving) {
      animationDelay = removalPosition * 500
    } else {
      animationDelay = bandsBeingRemoved * 500
    }
  }

  return animationDelay
}

/** Calculates progress through a pricing band as a percentage, returns  0-100 percentage if subtotal is between thresholds, 0 otherwise */
const calculateProgress = (
  subtotal: string,
  lowerThreshold: string,
  upperThreshold: string | null
) => {
  if (!subtotal) return 0
  const subtotalValue = parseFloat(subtotal)
  const lower = parseFloat(lowerThreshold)

  // Below the band range / At the lower threshold (start of band)
  if (subtotalValue < lower || subtotalValue === lower) return 0

  // Final band with null upperThreshold - if we've reached the threshold, it's 100%
  if (!upperThreshold) {
    return subtotalValue > lower ? 100 : 0
  }

  const upper = parseFloat(upperThreshold)

  // Above the band range (band is fully completed) / At the upper threshold (end of band)
  if (subtotalValue > upper || subtotalValue === upper) return 100

  // Between thresholds - calculate percentage progress
  return ((subtotalValue - lower) / (upper - lower)) * 100
}

/** Determines state for each rendered delivery pricing band in the UI */
const calculateBandData = (
  band: DeliveryPricingBands,
  subtotal: string | undefined,
  index: number,
  cartLoading: boolean,
  prevActiveCount: number,
  activeBandsCount: number,
  hasBandInProgress: boolean,
  totalBands: number
) => {
  const { lowerThreshold, upperThreshold } = band

  if (!subtotal && !cartLoading) {
    return {
      active: false,
      progress: 0,
      animationDelay: 0
    }
  }

  const isActive = cartLoading
    ? index < prevActiveCount
    : subtotal
      ? parseFloat(subtotal) > parseFloat(lowerThreshold)
      : false

  const progress =
    isActive && subtotal
      ? calculateProgress(subtotal, lowerThreshold, upperThreshold)
      : 0

  const animationDelay = calculateAnimationDelay(
    isActive,
    progress,
    index,
    prevActiveCount,
    activeBandsCount,
    hasBandInProgress
  )

  return {
    active: isActive,
    progress,
    animationDelay
  }
}

export {
  addDummyBand,
  getCurrentPricingBand,
  getNextPricingBand,
  calculateProgress,
  getActiveBands,
  getDiscountCopy,
  calculateBandData
}
